/**
 * NewsPulseAirlineProfile — rich public profile for a single airline.
 * Consumes /api/newspulse/v1/aviation/airline/{id}.
 *
 * Sections: hero (name, IATA/ICAO, country, alliance), core stats,
 * hub airports, routes operated, fleet, latest news, related airlines.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2, Plane, MapPin, Globe2, Newspaper, Network, Cpu, Users, ArrowRight,
} from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";

const ALLIANCE = {
  star_alliance: "Star Alliance",
  oneworld: "oneworld",
  skyteam: "SkyTeam",
  none: null,
};

function StatBox({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3">
      <span className="rounded-lg bg-violet-50 p-2 text-violet-700">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="text-lg font-bold text-slate-900">{value}</div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function AirportPill({ airport }) {
  return (
    <Link
      to={`/aviation/airport/${airport.airport_id}`}
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-blue-400 hover:text-blue-700 transition-colors"
      data-testid={`airline-airport-pill-${airport.airport_id}`}
    >
      <span className="inline-flex rounded-md bg-sky-50 border border-sky-100 text-sky-700 text-[10px] font-bold px-1.5 py-0.5">
        {airport.iata || "—"}
      </span>
      <span className="font-medium text-slate-800">{airport.canonical_name}</span>
    </Link>
  );
}

function RouteRow({ route }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 px-3 hover:bg-slate-50 transition-colors" data-testid={`airline-route-row-${route.route_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 shrink-0">
          {route.origin_iata}
        </span>
        <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="inline-flex rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 shrink-0">
          {route.destination_iata}
        </span>
        <span className="text-sm text-slate-800 truncate">{route.canonical_name}</span>
      </div>
      <span className={`text-[10px] font-semibold uppercase tracking-widest shrink-0 ${route.is_international ? "text-amber-600" : "text-emerald-600"}`}>
        {route.is_international ? "Intl" : "Domestic"}
      </span>
    </div>
  );
}

function AircraftCard({ aircraft }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`airline-fleet-${aircraft.aircraft_id}`}>
      <div className="flex items-start gap-2">
        <span className="rounded-md bg-indigo-50 p-1.5 text-indigo-700 shrink-0">
          <Cpu className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900 truncate">{aircraft.canonical_name}</div>
          <div className="text-[11px] text-slate-500">
            {aircraft.manufacturer}
            {aircraft.aircraft_class && ` · ${aircraft.aircraft_class.replace("_", " ")}`}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NewsPulseAirlineProfile() {
  const { airlineId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    npApi
      .airline(airlineId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e?.message || "load_failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [airlineId]);

  if (loading) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-16 text-slate-500" data-testid="airline-profile-loading">
        Loading airline…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-16" data-testid="airline-profile-error">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Airline not found</h1>
        <p className="text-slate-500 mb-4">We couldn't find "{airlineId}" in the aviation graph.</p>
        <Link to="/aviation" className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-800 font-semibold">
          ← Back to Aviation Hub
        </Link>
      </div>
    );
  }

  const a = data.airline;
  const hubAirports = data.hub_airports || [];
  const routes = data.routes || [];
  const fleet = data.fleet || [];
  const relatedAirlines = data.related_airlines || [];
  const events = data.recent_events || [];
  const fallbackEvents = data.fallback_events || [];
  const allianceLabel = ALLIANCE[a.alliance || "none"];

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8" data-testid="airline-profile">
      {/* Breadcrumb */}
      <nav className="text-xs text-slate-500 mb-3">
        <Link to="/aviation" className="hover:text-violet-700">Aviation Hub</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-700 font-medium">{a.canonical_name}</span>
      </nav>

      {/* Hero */}
      <section className="rounded-2xl bg-gradient-to-br from-slate-900 via-violet-900 to-purple-800 p-8 md:p-12 text-white mb-6 relative overflow-hidden">
        <div className="absolute -right-6 -bottom-10 opacity-10">
          <Building2 className="w-56 h-56" strokeWidth={0.6} />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex rounded-md bg-white/15 backdrop-blur-sm text-white text-xs font-bold px-2 py-0.5 border border-white/20">
              {a.iata || "—"}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-violet-200">
              {a.icao || ""}
            </span>
            {a.airline_type && (
              <span className="inline-flex rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold uppercase tracking-widest text-violet-200 px-2 py-0.5">
                {a.airline_type.replace("_", " ")}
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-4xl" data-testid="airline-profile-name">
            {a.canonical_name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-violet-100">
            {a.country_name && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> {a.country_name}
              </span>
            )}
            {allianceLabel && (
              <>
                <span className="text-violet-300">·</span>
                <span className="inline-flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> {allianceLabel}
                </span>
              </>
            )}
          </div>
          {(a.aliases || []).length > 0 && (
            <p className="mt-2 text-xs text-violet-200/80">
              Also known as: {(a.aliases || []).join(" · ")}
            </p>
          )}
        </div>
      </section>

      {/* Core stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="airline-stats">
        <StatBox label="Hub airports" value={hubAirports.length} icon={Building2} />
        <StatBox label="Routes operated" value={routes.length} icon={Network} />
        <StatBox label="Fleet types" value={fleet.length} icon={Cpu} />
        <StatBox label="Recent stories" value={events.length} icon={Newspaper} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: news + routes */}
        <div className="lg:col-span-2 space-y-8">
          {/* News */}
          <section data-testid="airline-news">
            <div className="pb-3 mb-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-violet-600" /> Latest stories
              </h2>
            </div>
            {events.length ? (
              <div>
                {events.map((e) => <EventCard key={e.event_id} event={e} />)}
              </div>
            ) : fallbackEvents.length ? (
              <>
                <p className="text-xs text-slate-500 italic mb-3">
                  No stories tagged to this airline yet — showing the latest across all aviation.
                </p>
                <div data-testid="airline-fallback-news">
                  {fallbackEvents.map((e) => <EventCard key={e.event_id} event={e} compact />)}
                </div>
              </>
            ) : (
              <p className="text-slate-500 italic text-sm">
                No stories yet tagged to this airline. NewsPulse will begin populating this
                feed as aviation events are ingested.
              </p>
            )}
          </section>

          {/* Routes */}
          <section data-testid="airline-routes">
            <div className="pb-3 mb-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                <Network className="w-5 h-5 text-sky-600" /> Routes operated
              </h2>
            </div>
            {routes.length ? (
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                {routes.slice(0, 40).map((r) => <RouteRow key={r.route_id} route={r} />)}
              </div>
            ) : (
              <p className="text-slate-500 italic text-sm">
                No canonical routes registered for this airline yet.
              </p>
            )}
          </section>
        </div>

        {/* Right: sidebar */}
        <aside className="space-y-6">
          {hubAirports.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Hub airports
              </h3>
              <div className="flex flex-wrap gap-2">
                {hubAirports.map((h) => <AirportPill key={h.airport_id} airport={h} />)}
              </div>
            </section>
          )}

          {fleet.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
                <Cpu className="w-4 h-4" /> Fleet
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {fleet.map((f) => <AircraftCard key={f.aircraft_id} aircraft={f} />)}
              </div>
            </section>
          )}

          {relatedAirlines.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2">
                <Globe2 className="w-4 h-4" /> {allianceLabel} partners
              </h3>
              <ul className="space-y-2">
                {relatedAirlines.map((ra) => (
                  <li key={ra.airline_id}>
                    <Link
                      to={`/aviation/airline/${ra.airline_id}`}
                      className="flex items-center gap-2 text-sm text-slate-700 hover:text-violet-700"
                      data-testid={`airline-related-${ra.airline_id}`}
                    >
                      <span className="inline-flex rounded-md bg-violet-50 border border-violet-100 text-violet-700 text-[10px] font-bold px-1.5 py-0.5">
                        {ra.iata || "—"}
                      </span>
                      <span className="truncate">{ra.canonical_name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
